import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { CheckCircle, XCircle, SpinnerGap } from "@phosphor-icons/react";

const PLAN_LABEL = { standard: "Offre Unique", monthly: "Mensuel", annual: "Annuel", lifetime: "À vie" };

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const returnTo = params.get("return_to");
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const poll = useCallback(async () => {
    if (!sessionId) { setStatus("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setPlan(data.plan);
        setStatus("paid");
        try { await api.post("/events", { event: "checkout_completed", props: { plan: data.plan, return_to: returnTo || null } }); } catch (_) { /* noop */ }
        refresh();
        return;
      }
      if (["expired", "failed"].includes(data.payment_status)) { setStatus("error"); return; }
      if (attempts >= 8) { setStatus("pending"); return; }
      setTimeout(() => setAttempts((a) => a + 1), 2000);
    } catch {
      setStatus("error");
    }
  }, [sessionId, attempts, refresh, returnTo]);

  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="min-h-screen bg-bg relative z-10 flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <div className="mb-8"><Logo size="text-2xl" /></div>
        <div className="surface rounded-xl p-10" data-testid="payment-success-panel">
          {status === "checking" && (
            <>
              <SpinnerGap size={48} className="text-accent mx-auto mb-4 animate-spin" />
              <h1 className="font-display text-2xl font-bold mb-2">Confirmation du paiement…</h1>
              <p className="text-muted text-sm">Merci de patienter quelques secondes.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle size={56} className="text-accent mx-auto mb-4" weight="fill" />
              <h1 className="font-display text-2xl font-bold mb-2">Abonnement activé</h1>
              <p className="text-muted text-sm mb-6">Votre plan <span className="text-accent">{PLAN_LABEL[plan] || plan}</span> interKO est actif. Moins d'intercontrat, plus de missions.</p>
              <button onClick={() => navigate(returnTo || "/app")} data-testid="go-to-app-btn" className="bg-accent text-black font-semibold px-6 py-3 rounded-md hover-lift w-full">{returnTo ? "Revenir à l'opportunité" : "Accéder à interKO"}</button>
            </>
          )}
          {status === "pending" && (
            <>
              <SpinnerGap size={48} className="text-accent mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold mb-2">Paiement en cours de traitement</h1>
              <p className="text-muted text-sm mb-6">Votre paiement est en cours de validation. Vous pouvez continuer.</p>
              <button onClick={() => navigate("/app")} className="bg-surface-2 border border-border text-fg font-semibold px-6 py-3 rounded-md hover-lift w-full">Continuer</button>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle size={56} className="text-destructive mx-auto mb-4" weight="fill" />
              <h1 className="font-display text-2xl font-bold mb-2">Paiement non confirmé</h1>
              <p className="text-muted text-sm mb-6">Un problème est survenu. Réessayez depuis la page Abonnement.</p>
              <button onClick={() => navigate("/app/billing")} className="bg-surface-2 border border-border text-fg font-semibold px-6 py-3 rounded-md hover-lift w-full">Retour à l'abonnement</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
